import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faFilter,
  faCheck,
  faCheckDouble,
  faTrash,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import './NotificationsPage.css';
import { API_ENDPOINTS } from '../../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import NotificationPreferences from './NotificationPreferences';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    unreadOnly: false,
    priority: null,
    collection: null
  });
  const [showPreferences, setShowPreferences] = useState(false);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [filters, page]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const params = new URLSearchParams({
        limit: ITEMS_PER_PAGE,
        skip: page * ITEMS_PER_PAGE,
        ...(filters.unreadOnly && { unread_only: 'true' }),
        ...(filters.priority && { priority: filters.priority }),
        ...(filters.collection && { collection: filters.collection })
      });

      const response = await axios.get(
        `${API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS}?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success') {
        if (page === 0) {
          setNotifications(response.data.data);
        } else {
          setNotifications(prev => [...prev, ...response.data.data]);
        }
        setHasMore(response.data.data.length === ITEMS_PER_PAGE);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const response = await axios.get(
        API_ENDPOINTS.NOTIFICATIONS.STATS,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.status === 'success') {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      await axios.post(
        API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notificationId),
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setNotifications(prev =>
        prev.map(n =>
          n.notification_id === notificationId
            ? { ...n, is_read: true }
            : n
        )
      );
      fetchStats();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      await axios.post(
        API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      fetchStats();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      await axios.delete(
        API_ENDPOINTS.NOTIFICATIONS.DELETE(notificationId),
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setNotifications(prev =>
        prev.filter(n => n.notification_id !== notificationId)
      );
      fetchStats();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const updateFilters = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#dc3545';
      case 'medium':
        return '#fd7e14';
      default:
        return '#6c757d';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high':
        return 'HIGH PRIORITY';
      case 'medium':
        return 'MEDIUM';
      default:
        return 'LOW';
    }
  };

  const getActionDescription = (notification) => {
    const fields = notification.changed_fields || [];
    const collection = notification.collection_name || '';
    
    // Contract-related actions (most common/important)
    if (fields.includes('is_signed') || fields.includes('client_signature')) {
      return 'Signed their contract';
    }
    if (fields.includes('first_viewed_at')) {
      return 'Opened their contract for the first time';
    }
    if (fields.includes('sent_for_signature') || fields.includes('is_contractout')) {
      return 'Contract sent for signature';
    }
    if (fields.includes('sent_for_signature_at')) {
      return 'Contract sent to client';
    }
    
    // Monetization changes
    if (fields.includes('Snap_Monetized')) {
      return 'Monetization status changed';
    }
    if (fields.includes('Snap_Star')) {
      return 'Snap Star status changed';
    }
    
    // Payment information
    if (fields.some(f => ['client_payout_percent', 'company_net_percent'].includes(f))) {
      return 'Payment split updated';
    }
    if (fields.some(f => ['payment_method', 'payee_name'].includes(f))) {
      return 'Payment details updated';
    }
    
    // Profile changes
    if (fields.some(f => ['First_Legal_Name', 'Last_Legal_Name'].includes(f))) {
      return 'Legal name updated';
    }
    if (fields.includes('Stage_Name')) {
      return 'Stage name updated';
    }
    
    // Contact information
    if (fields.includes('Phone_Number') || fields.includes('Phone')) {
      return 'Phone number updated';
    }
    if (fields.includes('Email_Address')) {
      return 'Email address updated';
    }
    
    // Assignment changes
    if (fields.includes('assigned_employees')) {
      return 'Team assignment changed';
    }
    
    // Social media updates
    if (fields.some(f => f.includes('IG_'))) {
      if (fields.includes('IG_Username')) return 'Instagram username updated';
      if (fields.includes('IG_Followers')) return 'Instagram followers updated';
      if (fields.includes('IG_Verified')) return 'Instagram verified';
      return 'Instagram profile updated';
    }
    if (fields.some(f => f.includes('TT_'))) {
      if (fields.includes('TT_Username')) return 'TikTok username updated';
      if (fields.includes('TT_Followers')) return 'TikTok followers updated';
      if (fields.includes('TT_Verified')) return 'TikTok verified';
      return 'TikTok profile updated';
    }
    if (fields.some(f => f.includes('YT_'))) {
      if (fields.includes('YT_Username')) return 'YouTube username updated';
      if (fields.includes('YT_Followers')) return 'YouTube subscribers updated';
      if (fields.includes('YT_Verified')) return 'YouTube verified';
      return 'YouTube profile updated';
    }
    if (fields.some(f => f.includes('Snap_'))) {
      if (fields.includes('Snap_Username')) return 'Snapchat username updated';
      if (fields.includes('Snap_Followers')) return 'Snapchat followers updated';
      return 'Snapchat profile updated';
    }
    
    // Collection-specific fallbacks
    if (collection === 'Contracts') {
      return 'Contract updated';
    }
    if (collection === 'PaymentInfo') {
      return 'Payment information updated';
    }
    if (collection === 'SocialMedia') {
      return 'Social media updated';
    }
    if (collection === 'ClientInfo') {
      return 'Client information updated';
    }
    
    // Generic fallback - clean up field names
    const cleanFieldName = (field) => {
      return field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toLowerCase();
    };
    
    const displayFields = fields
      .filter(f => !f.includes('.'))  // Skip nested fields
      .slice(0, 2)
      .map(cleanFieldName)
      .join(' and ');
    
    return displayFields ? `Updated ${displayFields}` : 'Profile updated';
  };

  // Get category for grouping notifications
  const getActionCategory = (notification) => {
    const fields = notification.changed_fields || [];
    const collection = notification.collection_name || '';
    
    // Group all contract-related actions together
    if (collection === 'Contracts' || 
        fields.some(f => ['is_signed', 'client_signature', 'first_viewed_at', 'sent_for_signature', 'is_contractout', 'sent_for_signature_at'].includes(f))) {
      return 'contract';
    }
    
    // Group payment actions
    if (collection === 'PaymentInfo' || 
        fields.some(f => ['client_payout_percent', 'company_net_percent', 'payment_method', 'payee_name'].includes(f))) {
      return 'payment';
    }
    
    // Group profile actions
    if (fields.some(f => ['First_Legal_Name', 'Last_Legal_Name', 'Stage_Name'].includes(f))) {
      return 'profile';
    }
    
    // Group contact actions
    if (fields.some(f => ['Phone_Number', 'Phone', 'Email_Address'].includes(f))) {
      return 'contact';
    }
    
    // Group social media by platform
    if (fields.some(f => f.includes('IG_'))) return 'instagram';
    if (fields.some(f => f.includes('TT_'))) return 'tiktok';
    if (fields.some(f => f.includes('YT_'))) return 'youtube';
    if (fields.some(f => f.includes('Snap_'))) return 'snapchat';
    
    return 'other';
  };

  // Group notifications by client and category
  const groupNotifications = (notificationList) => {
    if (!notificationList || notificationList.length === 0) return [];
    
    const grouped = [];
    let currentGroup = null;
    
    notificationList.forEach((notif) => {
      const category = getActionCategory(notif);
      const clientId = notif.client_id;
      
      // Check if this notification belongs to the current group
      // Group if: same client AND same category
      if (currentGroup && 
          currentGroup.clientId === clientId && 
          currentGroup.category === category) {
        currentGroup.notifications.push(notif);
        currentGroup.count++;
    } else {
        // Start a new group
        if (currentGroup) {
          grouped.push(currentGroup);
        }
        currentGroup = {
          clientId: clientId,
          clientName: notif.client_name,
          category: category,
          action: getActionDescription(notif),
          notifications: [notif],
          count: 1,
          priority: notif.priority,
          timestamp: notif.timestamp
        };
      }
    });
    
    // Add the last group
    if (currentGroup) {
      grouped.push(currentGroup);
    }
    
    return grouped;
  };

  if (showPreferences) {
    return <NotificationPreferences onClose={() => setShowPreferences(false)} />;
  }

  return (
    <div className="notif-page">
      <div className="notif-header">
        <div className="notif-title">
          <FontAwesomeIcon icon={faBell} />
          <h1>Notifications</h1>
        </div>
        <div className="notif-actions">
          <button
            className="notif-preferences-btn"
            onClick={() => setShowPreferences(true)}
          >
            <FontAwesomeIcon icon={faCog} />
            <span>Settings</span>
          </button>
          {stats && stats.unread > 0 && (
            <button className="notif-mark-all-btn" onClick={markAllAsRead}>
              <FontAwesomeIcon icon={faCheckDouble} />
              <span>Mark All Read</span>
            </button>
          )}
        </div>
      </div>

      {stats && (
        <div className="notif-stats">
          <div className="notif-stat-card">
            <div className="notif-stat-value">{stats.total}</div>
            <div className="notif-stat-label">Total</div>
          </div>
          <div className="notif-stat-card">
            <div className="notif-stat-value">{stats.unread}</div>
            <div className="notif-stat-label">Unread</div>
          </div>
          <div className="notif-stat-card priority-high">
            <div className="notif-stat-value">{stats.by_priority.high}</div>
            <div className="notif-stat-label">High Priority</div>
          </div>
          <div className="notif-stat-card priority-medium">
            <div className="notif-stat-value">{stats.by_priority.medium}</div>
            <div className="notif-stat-label">Medium Priority</div>
          </div>
          <div className="notif-stat-card priority-low">
            <div className="notif-stat-value">{stats.by_priority.low}</div>
            <div className="notif-stat-label">Low Priority</div>
          </div>
        </div>
      )}

      <div className="notif-filters">
        <button
          className={`notif-filter-btn ${filters.unreadOnly ? 'active' : ''}`}
          onClick={() => updateFilters('unreadOnly', !filters.unreadOnly)}
        >
          Unread Only
        </button>
        <select
          className="notif-filter-select"
          value={filters.priority || ''}
          onChange={(e) => updateFilters('priority', e.target.value || null)}
        >
          <option value="">All Priorities</option>
          <option value="high">High Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="low">Low Priority</option>
        </select>
        <select
          className="notif-filter-select"
          value={filters.collection || ''}
          onChange={(e) => updateFilters('collection', e.target.value || null)}
        >
          <option value="">All Collections</option>
          <option value="ClientInfo">Client Info</option>
          <option value="Contracts">Contracts</option>
          <option value="Leads">Leads</option>
          <option value="snap_monetization_forms">Monetization Forms</option>
        </select>
      </div>

      <div className="notif-list">
        {loading && page === 0 ? (
          <div className="notif-loading">Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className="notif-empty">
            <FontAwesomeIcon icon={faBell} size="3x" />
            <p>No notifications to display</p>
          </div>
        ) : (
          <>
            {groupNotifications(notifications).map((group, groupIndex) => (
              <div key={groupIndex} className="notif-card-group">
                {group.count > 1 ? (
                  <>
                    <div className={`notif-card grouped ${group.notifications[0].is_read ? 'read' : 'unread'}`}>
                <div
                  className="notif-priority-bar"
                        style={{ backgroundColor: getPriorityColor(group.priority) }}
                />
                <div className="notif-main">
                  <div className="notif-top">
                    <div className="notif-client-info">
                            <h3>{group.clientName}</h3>
                      <span
                        className="notif-priority-badge"
                              style={{ backgroundColor: getPriorityColor(group.priority) }}
                      >
                              {getPriorityLabel(group.priority)}
                      </span>
                          </div>
                          <div className="notif-actions-row">
                            <button
                              className="notif-action-btn expand-btn"
                              onClick={() => {
                                const newExpanded = new Set(expandedGroups);
                                if (newExpanded.has(groupIndex)) {
                                  newExpanded.delete(groupIndex);
                                } else {
                                  newExpanded.add(groupIndex);
                                }
                                setExpandedGroups(newExpanded);
                              }}
                              title={expandedGroups.has(groupIndex) ? "Collapse" : "Expand"}
                            >
                              {expandedGroups.has(groupIndex) ? '▲' : '▼'}
                            </button>
                          </div>
                        </div>
                        <div className="notif-details">
                          <div className="notif-action-text">
                            <strong>{group.action}</strong> ({group.count} updates)
                          </div>
                          <div className="notif-meta">
                            <span className="notif-actor">Changed by: {group.notifications[0].actor_name}</span>
                            <span className="notif-time">{formatTimestamp(group.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {expandedGroups.has(groupIndex) && (
                      <div className="notif-group-items">
                        {group.notifications.map((notif) => (
                          <div key={notif.notification_id} className="notif-sub-item">
                            <div className="notif-sub-content">
                              <div className="notif-action-text">
                                {getActionDescription(notif)}
                              </div>
                              <div className="notif-meta">
                                <span className="notif-time">{formatTimestamp(notif.timestamp)}</span>
                              </div>
                    </div>
                    <div className="notif-actions-row">
                      {!notif.is_read && (
                        <button
                          className="notif-action-btn read-btn"
                          onClick={() => markAsRead(notif.notification_id)}
                          title="Mark as read"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                      )}
                      <button
                        className="notif-action-btn delete-btn"
                        onClick={() => deleteNotification(notif.notification_id)}
                                title="Delete"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className={`notif-card ${group.notifications[0].is_read ? 'read' : 'unread'}`}
                  >
                    <div
                      className="notif-priority-bar"
                      style={{ backgroundColor: getPriorityColor(group.notifications[0].priority) }}
                    />
                    <div className="notif-main">
                      <div className="notif-top">
                        <div className="notif-client-info">
                          <h3>{group.notifications[0].client_name}</h3>
                          <span
                            className="notif-priority-badge"
                            style={{ backgroundColor: getPriorityColor(group.notifications[0].priority) }}
                          >
                            {getPriorityLabel(group.notifications[0].priority)}
                          </span>
                        </div>
                        <div className="notif-actions-row">
                          {!group.notifications[0].is_read && (
                            <button
                              className="notif-action-btn read-btn"
                              onClick={() => markAsRead(group.notifications[0].notification_id)}
                              title="Mark as read"
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                          )}
                          <button
                            className="notif-action-btn delete-btn"
                            onClick={() => deleteNotification(group.notifications[0].notification_id)}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                  <div className="notif-details">
                    <div className="notif-action-text">
                          <strong>{getActionDescription(group.notifications[0])}</strong>
                    </div>
                    <div className="notif-meta">
                          <span className="notif-actor">Changed by: {group.notifications[0].actor_name}</span>
                          <span className="notif-time">{formatTimestamp(group.notifications[0].timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {hasMore && (
              <button
                className="notif-load-more-btn"
                onClick={loadMore}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;

